import { useEffect, useState } from "react";
import { Menu, X, ArrowUpRight, Languages } from "lucide-react";
import { BrandLogo } from "@/components/site/BrandLogo";
import { useLang } from "@/lib/i18n";

export const Header = () => {
  const { lang, t, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const NAV = [
    { label: t.nav.capabilities, href: "#capabilities" },
    { label: t.nav.about, href: "#about" },
    { label: t.nav.approach, href: "#process" },
    { label: t.nav.contact, href: "#contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const toggleLang = () => setLang(lang === "en" ? "ar" : "en");

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "border-b border-[color:var(--rule)] bg-[color:var(--paper)]/85 backdrop-blur-xl"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-[1200px] items-center justify-between px-6 py-4 lg:px-10">
        <a
          href="#top"
          data-testid="nav-logo"
          className={`flex items-center gap-2 transition-all duration-500 ${
            scrolled
              ? "opacity-100 translate-y-0"
              : "-translate-y-1 opacity-0 pointer-events-none"
          }`}
          aria-label="Delhigence.AI"
        >
          <BrandLogo className="h-8" tone="ink" testId="header-logo" />
        </a>

        <nav
          className="hidden items-center gap-9 lg:flex"
          data-testid="nav-desktop"
        >
          {NAV.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className="group relative text-[13px] font-medium text-[color:var(--ink-2)] transition-colors hover:text-[color:var(--emerald)]"
            >
              {item.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-[color:var(--gold)] transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2 lg:gap-3">
          <button
            type="button"
            data-testid="nav-lang-toggle"
            onClick={toggleLang}
            aria-label={t.nav.langAlt}
            className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--rule-strong)] bg-[color:var(--paper)]/70 px-3 py-2 text-[11px] font-mono-accent uppercase tracking-[0.22em] text-[color:var(--ink-2)] transition-all hover:border-[color:var(--emerald)] hover:text-[color:var(--emerald)]"
          >
            <Languages className="h-3.5 w-3.5" />
            {lang === "en" ? "AR" : "EN"}
          </button>

          <a
            href="#contact"
            data-testid="nav-cta-book-demo"
            className="hidden lg:inline-flex group items-center gap-2 rounded-full bg-[color:var(--emerald)] px-5 py-2.5 text-[13px] font-medium text-[color:var(--paper)] transition-all hover:bg-[color:var(--ink)] hover:shadow-[0_14px_30px_-14px_rgba(13,59,46,0.6)]"
          >
            {t.nav.cta}
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>

          <button
            type="button"
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
            className="rounded-md p-2 text-[color:var(--ink)] lg:hidden"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          className="border-t border-[color:var(--rule)] bg-[color:var(--paper)] lg:hidden"
          data-testid="nav-mobile-panel"
        >
          <div className="flex flex-col gap-1 px-6 py-4">
            {NAV.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setOpen(false)}
                className="rounded-md px-2 py-3 text-base text-[color:var(--ink)] hover:bg-[color:var(--ivory-2)] hover:text-[color:var(--emerald)]"
              >
                {item.label}
              </a>
            ))}
            <a
              href="#contact"
              onClick={() => setOpen(false)}
              className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--emerald)] px-5 py-3 text-sm font-medium text-[color:var(--paper)]"
            >
              {t.nav.cta}
              <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
